import { useCallback, useEffect, useMemo, useState } from 'react'

import { getSalesLensCategories } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { IAbstractLensItem } from '@/types'
import { SalesLensCategoryModel } from '@/types/clarityLens'
import { areFilterParamsValid } from '@/utils/filterParams'
import { dateToString } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

export default function useSalesLensCategories(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () => areFilterParamsValid(startDate, endDate, branches),
    [startDate, endDate, branches]
  )
  const loading = !isReady || isFetching

  const [categoriesData, setCategoriesData] =
    useState<SalesLensCategoryModel[]>()
  const [trendCategoriesData, setTrendCategoriesData] =
    useState<SalesLensCategoryModel[]>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { data } = await fetchCategoriesData(branches, startDate, endDate)
        setCategoriesData(data)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [branches, startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange
        const { data } = await fetchCategoriesData(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )
        setTrendCategoriesData(data)
      } catch (err) {
        const error = new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      }
    }
  }, [branches, startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [fetchData, fetchTrendData, isReady])

  const refetch = useCallback(() => {
    console.log('Refetching sales lens categories...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  // Format categories data for display with trend data
  const formattedCategoriesData = useMemo(() => {
    if (!categoriesData || !trendCategoriesData) return []

    // Create a map of trend data by category name for easy lookup
    const trendDataMap = new Map(
      trendCategoriesData.map((category) => [category.subSubCategory, category])
    )

    const formattedData = categoriesData.map(
      (category: SalesLensCategoryModel): IAbstractLensItem<any> => {
        const trendCategory = trendDataMap.get(category.subSubCategory)

        return {
          item: category.subSubCategory,
          current: {
            netSalesAmount: category.netSalesAmount,
            netSalesPercentage: category.netSalesPercentage,
            unitSold: category.unitSold,
            avgSellingPrice: category.avgSellingPrice,
            grossToDiscountPercentage: category.grossToDiscountPercentage,
          },
          prev: trendCategory
            ? {
                netSalesAmount: trendCategory.netSalesAmount,
                netSalesPercentage: trendCategory.netSalesPercentage,
                unitSold: trendCategory.unitSold,
                avgSellingPrice: trendCategory.avgSellingPrice,
                grossToDiscountPercentage:
                  trendCategory.grossToDiscountPercentage,
              }
            : undefined,
        }
      }
    )

    return formattedData.sort((a, b) => {
      return b.current.netSalesAmount - a.current.netSalesAmount
    })
  }, [categoriesData, trendCategoriesData])

  return {
    formattedCategoriesData,
    loading,
    refetch,
  }
}

async function fetchCategoriesData(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  data: SalesLensCategoryModel[]
}> {
  try {
    const response = await getSalesLensCategories(branches, startDate, endDate)

    const data: SalesLensCategoryModel[] = response.map((responseItem) => {
      return convertData(
        responseItem.sub_sub_category,
        responseItem.net_sales_amount,
        responseItem.net_sales_percentage,
        responseItem.quantity_sold,
        responseItem.gross_to_discount_percentage
      )
    })

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  subSubCategory: string,
  netSalesAmount: number,
  netSalesPercentage: number,
  quantitySold: number,
  grossToDiscountPercentage: number
): SalesLensCategoryModel {
  return {
    subSubCategory,
    netSalesAmount: formatMetric(netSalesAmount, 2),
    netSalesPercentage: formatMetric(netSalesPercentage, 2),
    unitSold: formatMetric(quantitySold, 2),
    avgSellingPrice: formatMetric(netSalesAmount / quantitySold, 2),
    grossToDiscountPercentage: formatMetric(grossToDiscountPercentage, 2),
  }
}
